import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  ArrowUpRight,
  Play,
  Pause,
  HeartHandshake,
  Sparkles,
  Compass,
  Receipt,
  AlertTriangle,
  BarChart3,
} from "lucide-react";
import Seo from "@/components/Seo";

const ACCENT = "#E7C66A";
const INK = "#0E1830";
const CREAM = "#F1ECE0";

const PILLARS = [
  {
    num: "01",
    kicker: "Welcome",
    heading: "Every guest, personally welcomed.",
    body: "A warm, tailored start to every stay — from the moment they arrive.",
    Icon: HeartHandshake,
  },
  {
    num: "02",
    kicker: "Loyalty",
    heading: "Loyal guests, rewarded.",
    body: "Recognise your repeat guests and turn them into regulars.",
    Icon: Sparkles,
  },
  {
    num: "03",
    kicker: "Guest Guide",
    heading: "Everything they need, in one place.",
    body: "Local tips, help nearby and things to do — all under your brand.",
    Icon: Compass,
  },
  {
    num: "04",
    kicker: "Extra Revenue",
    heading: "More revenue from every stay.",
    body: "Simple in-room purchases that add up, night after night.",
    Icon: Receipt,
  },
  {
    num: "05",
    kicker: "Peace of Mind",
    heading: "Hear about issues first.",
    body: "Fix problems quickly — before they turn into bad reviews.",
    Icon: AlertTriangle,
  },
  {
    num: "06",
    kicker: "Insight",
    heading: "Your property at a glance.",
    body: "Clear insight into your guests, sales and reviews.",
    Icon: BarChart3,
  },
];

function TeaserPlayer() {
  const [playing, setPlaying] = useState(false);
  return (
    <div
      className="relative rounded-sm overflow-hidden border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
      data-testid="gida-teaser"
    >
      <video
        src="/products/gida-teaser.mp4"
        className="w-full h-auto aspect-video object-cover block"
        playsInline
        preload="metadata"
        poster=""
        controls={playing}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        id="gida-teaser-video"
      />
      {!playing && (
        <button
          type="button"
          onClick={() => {
            const el = document.getElementById("gida-teaser-video");
            if (el) {
              el.play();
            }
          }}
          data-testid="gida-teaser-play"
          className="absolute inset-0 flex items-center justify-center bg-[#0E1830]/40 hover:bg-[#0E1830]/55 transition-colors"
        >
          <span
            className="h-20 w-20 md:h-24 md:w-24 rounded-full flex items-center justify-center transition-transform hover:scale-110"
            style={{ background: ACCENT }}
          >
            <Play size={34} className="text-[#0E1830] ml-1" fill="#0E1830" />
          </span>
        </button>
      )}
    </div>
  );
}

export default function GidaProductPage() {
  return (
    <div className="min-h-screen" style={{ background: INK, color: CREAM }}>
      <Seo
        title="GIDA — Digital Guest Experience Platform"
        description="GIDA, built and powered by OpsIQ. A digital guest experience platform for hotels, guesthouses and short-stay homes: personal welcomes, loyalty rewards, in-room purchases, insight. Book a private demo."
        path="/products/gida"
        image="/products/og-gida.jpg"
        imageAlt="GIDA — Your stay. Your space. Your experience."
      />
      {/* Top bar */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 pt-8 md:pt-10 flex items-center justify-between">
        <Link
          to="/products"
          className="inline-flex items-center gap-2 text-[11px] font-plex-mono uppercase tracking-[0.22em] text-white/60 hover:text-[#E7C66A] transition-colors"
          data-testid="gida-back"
        >
          <ArrowLeft size={14} /> Back to Products
        </Link>
        <Link
          to="/"
          className="text-sm font-semibold tracking-[0.2em] uppercase text-white/80 hover:text-[#E7C66A] transition-colors"
        >
          OpsIQ
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-12 md:pt-20 pb-16 md:pb-24">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div
              className="text-[11px] font-plex-mono uppercase tracking-[0.26em] mb-6 inline-flex items-center gap-2"
              style={{ color: ACCENT }}
            >
              <span
                className="inline-block h-1.5 w-1.5 rounded-full"
                style={{ background: ACCENT }}
              />
              Hospitality · Built & powered by OpsIQ
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="font-editorial text-6xl md:text-8xl lg:text-9xl leading-[0.9] tracking-tight"
            >
              GIDA.
            </motion.h1>
            <div
              className="font-editorial italic text-2xl md:text-3xl lg:text-4xl mt-5 max-w-2xl leading-tight"
              style={{ color: ACCENT }}
            >
              Your stay. Your space. Your experience.
            </div>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg leading-relaxed text-white/80 max-w-xl">
              A digital guest experience platform for hotels, guesthouses and short-stay homes. Personalise every welcome,
              reward loyal guests, unlock extra revenue — and know your property at a glance.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/book?product=gida"
                data-testid="gida-cta-demo"
                className="inline-flex items-center gap-2 px-6 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
                style={{ background: ACCENT, color: INK }}
              >
                Book a private demo <ArrowUpRight size={16} />
              </Link>
              <a
                href="https://wa.me/233546080065?text=Hello%20OpsIQ%20%E2%80%94%20I%27d%20like%20a%20GIDA%20demo%20for%20my%20property."
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-6 py-4 border text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
                style={{ borderColor: `${CREAM}3D`, color: CREAM }}
              >
                WhatsApp us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Teaser video */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-16 md:pb-24">
        <TeaserPlayer />
      </section>

      {/* 6 Pillars */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pb-20 md:pb-28">
        <div className="flex items-baseline justify-between flex-wrap gap-6 mb-10 md:mb-16">
          <div className="max-w-3xl">
            <div
              className="text-[11px] font-plex-mono uppercase tracking-[0.26em] mb-5"
              style={{ color: ACCENT }}
            >
              Six pillars
            </div>
            <h2 className="font-editorial text-4xl md:text-5xl leading-tight tracking-tight">
              Six ways GIDA quietly makes <span style={{ color: ACCENT }} className="italic">every stay better.</span>
            </h2>
          </div>
          <div className="text-sm text-white/55 max-w-sm">
            Each pillar runs on your branded property hub. Guests scan one QR code — the rest happens by itself.
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PILLARS.map((p, i) => (
            <motion.div
              key={p.num}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
              className="border border-white/10 p-7 md:p-8 bg-white/[0.03] hover:bg-white/[0.06] transition-colors"
            >
              <div className="flex items-center justify-between mb-6">
                <span
                  className="font-plex-mono text-[11px] uppercase tracking-[0.26em]"
                  style={{ color: ACCENT }}
                >
                  {p.num} / {p.kicker}
                </span>
                <p.Icon size={18} strokeWidth={1.5} style={{ color: ACCENT }} />
              </div>
              <h3 className="font-editorial text-2xl leading-tight tracking-tight mb-3">
                {p.heading}
              </h3>
              <p className="text-sm leading-relaxed text-white/65">{p.body}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="relative border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 md:py-28 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <div
              className="text-[11px] font-plex-mono uppercase tracking-[0.26em] mb-5"
              style={{ color: ACCENT }}
            >
              See GIDA in action
            </div>
            <h2 className="font-editorial text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Book a private demo for <span className="italic" style={{ color: ACCENT }}>your property.</span>
            </h2>
            <p className="mt-5 max-w-xl text-white/70">
              Tell us a little about your property and we'll set up a guided demo with your rooms, your branding and your
              services — no commitment.
            </p>
          </div>
          <div className="lg:col-span-4 flex flex-col gap-3">
            <Link
              to="/book?product=gida"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
              style={{ background: ACCENT, color: INK }}
            >
              Request demo <ArrowUpRight size={16} />
            </Link>
            <a
              href="tel:+233546080065"
              className="inline-flex items-center justify-center gap-2 px-6 py-4 border text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
              style={{ borderColor: `${CREAM}3D`, color: CREAM }}
            >
              Call +233 54 608 0065
            </a>
            <div className="text-[10px] font-plex-mono uppercase tracking-[0.22em] text-white/40 text-center mt-2">
              Built & powered by OpsIQ · opsiq.live
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
